import { composeProgress } from "apollo/utils";
import {
  GET_AUCTION_FORMS,
  GET_REHAB_FORMS,
  GET_TRANSITIONAL_CARE,
} from "core/apollo/graphql";
import {
  DOCS_FORM_NAMES,
  SEARCH_ACTION_DELETE_FORM,
  SEARCH_ACTION_DELETE_REHAB_FORMS,
  SEARCH_ACTION_DELETE_TRANSITIONAL_CARE_FORM,
} from "core/consts";
import { useApolloEncryptionContext } from "core/model/patients/encryption/utils";
import { getErrorMessage } from "core/model/utils/errors";
import { Auction, DocsFormName, Patient, RehabFormName } from "core/types";
import { useDeleteUnsharedFileByType } from "dsl/ecosystems/PatientFiles/useDeleteUnsharedFileByType";
import { getRehabFormConfig } from "dsl/ecosystems/PatientForms/RehabForms/configs";
import useSearchAction from "dsl/hooks/useSearchAction";
import { FORM_NAME_TO_FILE_TYPE } from "./utils";

export function useDeleteConfirmation({
  auction,
  formName,
  patientId,
}: {
  auction: Auction;
  formName: DocsFormName;
  patientId: Patient["id"];
}) {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const [deleteRehabForm, deleteRehabFormProgress, resetProgress] =
    useSearchAction({
      actionType: SEARCH_ACTION_DELETE_REHAB_FORMS,
      refetchQueries: [
        {
          context: { encryptionContext },
          query: GET_REHAB_FORMS,
          variables: { auctionId: auction.id, patientId },
        },
      ],
    });
  const [deleteTransitionalCareForm, deleteTransitionalCareFormProgress] =
    useSearchAction({
      actionType: SEARCH_ACTION_DELETE_TRANSITIONAL_CARE_FORM,
    });

  const [deleteForm, deleteFormProgress] = useSearchAction({
    actionType: SEARCH_ACTION_DELETE_FORM,
    refetchQueries: [
      {
        context: { encryptionContext },
        query: GET_AUCTION_FORMS,
        variables: {
          auctionId: auction.id,
        },
      },
    ],
  });

  const { formSubType, formType } =
    formName === DOCS_FORM_NAMES.TRANSITIONAL_CARE
      ? { formSubType: undefined, formType: undefined }
      : getRehabFormConfig(formName as RehabFormName) ?? {};

  const { deleteUnsharedFile } = useDeleteUnsharedFileByType({
    auctionId: auction.id,
    formSubType,
    formType,
  });

  const onDelete = async () => {
    try {
      switch (formName) {
        case DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT:
        case DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY:
          await deleteForm({
            auction,
            context: {
              form_type: FORM_NAME_TO_FILE_TYPE[formName],
            },
          });
          break;
        case DOCS_FORM_NAMES.TRANSITIONAL_CARE:
          await deleteTransitionalCareForm({
            auction,
            onCompleted: (_, client) => {
              client.writeQuery({
                data: {
                  auction: {
                    id: auction.id,
                    seald_encryption_context: auction.seald_encryption_context,
                    session_key_context: auction.session_key_context,
                    transitional_care: null,
                  },
                },
                query: GET_TRANSITIONAL_CARE,
                variables: {
                  auctionId: auction.id,
                  patientId: auction.patient.id,
                },
              });
            },
          });
          break;
        default:
          await deleteRehabForm({
            auction,
            context: {
              rehab_form_type: formType,
            },
          });
          break;
      }

      await deleteUnsharedFile();
      resetProgress();
    } catch (err) {
      console.error(`error deleting ${formName} form: ${getErrorMessage(err)}`);
    }
  };

  return {
    deleteFormProgress: composeProgress([
      deleteRehabFormProgress,
      deleteTransitionalCareFormProgress,
      deleteFormProgress,
    ]),
    onDelete,
  };
}
